import { useAssistantRuntime } from '@assistant-ui/react'
import { MessageCircle } from 'lucide-react'
import { createContext, type RefObject, useContext, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'

export interface SchemaQuestion {
  label: string
  scope: 'working' | 'proposal'
}

export const SchemaConversationContext = createContext<((question: SchemaQuestion) => void) | null>(
  null
)

export function AskSchemaAgent({
  label,
  scope = 'working',
}: {
  label: string
  scope?: SchemaQuestion['scope']
}) {
  const ask = useContext(SchemaConversationContext)
  const { t } = useTranslation()
  if (!ask) return null
  return (
    <Button
      size="sm"
      variant="outline"
      className="my-3 h-auto max-w-full self-start whitespace-normal py-2 text-left"
      onClick={() => ask({ label, scope })}
    >
      <MessageCircle className="size-4" />
      {t('schemaAgent.workbench.askAbout', { label })}
    </Button>
  )
}

/** Runs inside Assistant UI; selecting an item prepares a question without sending it. */
export function SchemaQuestionComposer({
  question,
  container,
}: {
  question: SchemaQuestion | null
  container: RefObject<HTMLDivElement | null>
}) {
  const runtime = useAssistantRuntime()
  const { t } = useTranslation()
  const handled = useRef<SchemaQuestion | null>(null)
  useEffect(() => {
    if (!question || handled.current === question) return
    handled.current = question
    const current = runtime.thread.composer.getState().text
    const prompt = t(`schemaAgent.workbench.question.${question.scope}`, { label: question.label })
    runtime.thread.composer.setText(current.trim() ? `${current}\n\n${prompt}` : prompt)
    const input = container.current?.querySelector('textarea')
    input?.focus()
    input?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [question, runtime, t, container])
  return null
}
